import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DnsCreateRecordDialog } from "@/features/dns/components/DnsCreateRecordDialog";
import { DnsEditRecordDialog } from "@/features/dns/components/DnsEditRecordDialog";
import { DnsRecordsTable } from "@/features/dns/components/DnsRecordsTable";
import {
  useConfiguredDnsZone,
  useDnsRecordMutations,
  useDnsRecords,
  useDnsZonesList,
} from "@/features/dns/hooks/useDns";
import type { IDnsRecord } from "@/features/dns/schemas/dns";
import type { DnsRecordsQuery } from "@/features/dns/services/dnsService";

const PER_PAGE = 25;

export default function DnsPage() {
  const { data: configured, isLoading: loadingZone, error: zoneError } = useConfiguredDnsZone();
  const { data: zonesList, isError: zonesListError } = useDnsZonesList(true);

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [draftType, setDraftType] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [filters, setFilters] = useState({ type: "", name: "", content: "" });

  const [editRecord, setEditRecord] = useState<IDnsRecord | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IDnsRecord | null>(null);

  useEffect(() => {
    if (configured?.id && selectedZoneId === null) {
      setSelectedZoneId(configured.id);
    }
  }, [configured?.id, selectedZoneId]);

  const query: DnsRecordsQuery = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(filters.type.trim() ? { type: filters.type.trim() } : {}),
      ...(filters.name.trim() ? { name: filters.name.trim() } : {}),
      ...(filters.content.trim() ? { content: filters.content.trim() } : {}),
    }),
    [page, filters]
  );

  const zoneIdForApi =
    configured?.id && selectedZoneId === configured.id ? null : selectedZoneId;

  const mutations = useDnsRecordMutations(zoneIdForApi);

  const listEnabled = !loadingZone && selectedZoneId !== null;
  const { data: listData, isLoading: listLoading, error: listError } = useDnsRecords(
    configured?.id,
    selectedZoneId ?? undefined,
    query,
    listEnabled
  );

  const zoneOptions = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    if (configured) {
      map.set(configured.id, {
        id: configured.id,
        label: configured.name
          ? `${configured.name} (predeterminada)`
          : `Zona ${configured.id} (predeterminada)`,
      });
    }
    if (zonesList) {
      for (const z of zonesList) {
        if (!map.has(z.id)) {
          map.set(z.id, { id: z.id, label: `${z.name} (${z.status})` });
        }
      }
    }
    return [...map.values()];
  }, [configured, zonesList]);

  const totalPages = listData
    ? Math.max(
        1,
        Math.ceil(listData.meta.total_count / (listData.meta.per_page || PER_PAGE))
      )
    : 1;

  const applyFilters = () => {
    setFilters({
      type: draftType,
      name: draftName,
      content: draftContent,
    });
    setPage(1);
  };

  const openEdit = (r: IDnsRecord) => {
    setEditRecord(r);
    setEditOpen(true);
  };

  if (zoneError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">DNS Cloudflare</h1>
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle>No se pudo cargar la zona</CardTitle>
            <CardDescription>
              {(zoneError as Error).message ||
                "Comprueba VITE_API_URL, permisos de administrador y la configuración del servicio DNS."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">DNS Cloudflare</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Gestión de registros DNS mediante la API del servicio (zona por defecto o zonas
            accesibles con el token).
          </p>
          {zonesListError && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              No se pudo listar todas las zonas; solo se muestra la zona predeterminada del
              servidor.
            </p>
          )}
        </div>
        <DnsCreateRecordDialog mutations={mutations} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Zona</CardTitle>
          <CardDescription>Selecciona la zona cuyos registros quieres ver o editar.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-md space-y-2">
            <Label htmlFor="dns-zone">Zona de Cloudflare</Label>
            <select
              id="dns-zone"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              disabled={loadingZone || !selectedZoneId}
              value={selectedZoneId ?? ""}
              onChange={(e) => {
                setSelectedZoneId(e.target.value);
                setPage(1);
              }}
            >
              {zoneOptions.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
          <CardDescription>
            Los filtros se envían a la API de Cloudflare (type, name, content).
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-2 flex-1 min-w-[140px]">
            <Label htmlFor="f-type">Tipo</Label>
            <Input
              id="f-type"
              placeholder="A, CNAME…"
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
            />
          </div>
          <div className="space-y-2 flex-1 min-w-[160px]">
            <Label htmlFor="f-name">Nombre</Label>
            <Input
              id="f-name"
              placeholder="subdominio o FQDN"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />
          </div>
          <div className="space-y-2 flex-1 min-w-[160px]">
            <Label htmlFor="f-content">Contenido</Label>
            <Input
              id="f-content"
              placeholder="IP o destino"
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
            />
          </div>
          <Button type="button" onClick={applyFilters}>
            Aplicar filtros
          </Button>
        </CardContent>
      </Card>

      {listError && (
        <p className="text-sm text-destructive">
          {(listError as Error).message || "Error al cargar los registros."}
        </p>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-base">Registros</CardTitle>
            <CardDescription>
              {listData
                ? `${listData.meta.total_count} registro(s) — página ${listData.meta.page} de ${totalPages}`
                : "—"}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1 || listLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={listLoading || !listData || page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DnsRecordsTable
            records={listData?.records ?? []}
            isLoading={listLoading}
            mutations={mutations}
            onEdit={openEdit}
            deleteTarget={deleteTarget}
            onDeleteTargetChange={setDeleteTarget}
          />
        </CardContent>
      </Card>

      <DnsEditRecordDialog
        open={editOpen}
        onOpenChange={(o) => {
          setEditOpen(o);
          if (!o) setEditRecord(null);
        }}
        record={editRecord}
        mutations={mutations}
      />
    </div>
  );
}
